import { useEffect, useMemo, useRef, useState } from "react";

const X_WIDGET_SCRIPT_ID = "x-widgets-js";
const X_WIDGET_SCRIPT_SRC = "https://platform.x.com/widgets.js";
let xWidgetsPromise;

function extractPostId(postUrl) {
  if (typeof postUrl !== "string") return null;
  return postUrl.match(/\/status(?:es)?\/(\d+)/i)?.[1] || null;
}

function resolveWidgetsApi(resolve) {
  const twttr = window.twttr;

  if (!twttr) {
    resolve(null);
    return;
  }

  if (typeof twttr.ready === "function") {
    twttr.ready(() => resolve(window.twttr || twttr));
    return;
  }

  resolve(twttr);
}

function getXWidgets() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve(null);
  }

  if (window.twttr?.widgets?.createTweet) {
    return Promise.resolve(window.twttr);
  }

  if (xWidgetsPromise) return xWidgetsPromise;

  xWidgetsPromise = new Promise((resolve) => {
    const finish = () => resolveWidgetsApi(resolve);
    const fail = () => {
      xWidgetsPromise = undefined;
      resolve(null);
    };

    const existingScript =
      document.getElementById(X_WIDGET_SCRIPT_ID) ||
      document.querySelector(`script[src="${X_WIDGET_SCRIPT_SRC}"]`);

    if (existingScript) {
      if (window.twttr) {
        finish();
        return;
      }

      existingScript.addEventListener("load", finish, { once: true });
      existingScript.addEventListener("error", fail, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = X_WIDGET_SCRIPT_ID;
    script.src = X_WIDGET_SCRIPT_SRC;
    script.async = true;
    script.charset = "utf-8";
    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", fail, { once: true });
    document.body.appendChild(script);
  });

  return xWidgetsPromise;
}

export default function XPostEmbed({ postUrl, className = "", sponsored = false }) {
  const linkRel = sponsored ? "sponsored noopener noreferrer" : "noopener noreferrer";
  const embedContainerRef = useRef(null);
  const renderSequenceRef = useRef(0);
  const postId = useMemo(() => extractPostId(postUrl), [postUrl]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const container = embedContainerRef.current;
    const renderSequence = renderSequenceRef.current + 1;
    renderSequenceRef.current = renderSequence;

    if (container) container.replaceChildren();

    if (!postId) {
      setStatus("error");
      return undefined;
    }

    let active = true;
    setStatus("loading");

    getXWidgets().then(async (twttr) => {
      if (
        !active ||
        renderSequenceRef.current !== renderSequence ||
        !embedContainerRef.current
      ) {
        return;
      }

      const target = embedContainerRef.current;
      target.replaceChildren();

      if (!twttr?.widgets?.createTweet) {
        setStatus("error");
        return;
      }

      try {
        const widget = await twttr.widgets.createTweet(postId, target, {
          align: "center",
          cards: "visible",
          conversation: "none",
          dnt: true,
          theme: "light",
        });

        if (
          !active ||
          renderSequenceRef.current !== renderSequence ||
          !embedContainerRef.current
        ) {
          widget?.remove?.();
          return;
        }

        setStatus(widget ? "loaded" : "error");
      } catch {
        if (active && renderSequenceRef.current === renderSequence) {
          setStatus("error");
        }
      }
    });

    return () => {
      active = false;
    };
  }, [postId]);

  if (!postUrl) return null;

  return (
    <section
      className={`mt-8 min-w-0 overflow-visible rounded-[1.8rem] border border-neutral-200 bg-neutral-50 p-4 sm:p-5 ${className}`}
      aria-label="Original X post"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500">
          Original X post
        </h2>
        <a
          href={postUrl}
          target="_blank"
          rel={linkRel}
          className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-sm font-black text-neutral-800 transition hover:border-blue-200 hover:text-blue-700"
        >
          Open on X
        </a>
      </div>

      <div className="mx-auto w-full max-w-[560px] min-w-0 overflow-visible">
        <div className="relative min-h-[120px] w-full overflow-visible">
          <div
            ref={embedContainerRef}
            className="x-post-embed min-h-[120px] w-full overflow-visible"
            data-x-post-id={postId || undefined}
          />

          {status !== "loaded" && (
            <div
              className="absolute inset-0 z-10 flex min-h-[120px] items-center justify-center rounded-[1.4rem] border border-neutral-200 bg-white p-5 text-center"
              aria-live="polite"
            >
              {status === "loading" ? (
                <p className="text-sm font-semibold text-neutral-500">
                  Loading the full X post…
                </p>
              ) : (
                <a
                  href={postUrl}
                  target="_blank"
                  rel={linkRel}
                  className="text-sm font-black text-blue-700 underline decoration-blue-200 underline-offset-4"
                >
                  View the full post on X
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
